// Admin panel — pending cancel requests submitted by customers after driver acceptance
// (T&C §5). Admin can approve (which cancels the order) or reject the request.
import { useEffect, useState } from "react";
import api, { extractError } from "@/api/client";
import { X, RefreshCw, XCircle, CheckCircle2, Clock, MessageSquare } from "lucide-react";

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("ar-LY", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

export default function CancelRequestsModal({ onClose }) {
  const [requests, setRequests] = useState([]);
  const [statusTab, setStatusTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [noteMap, setNoteMap] = useState({});

  const load = async (s = statusTab) => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get(`/admin/cancel-requests?status=${s}`);
      setRequests(data.requests || []);
    } catch (e) { setErr(extractError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(statusTab); /* eslint-disable-next-line */ }, [statusTab]);

  const decide = async (req, decision) => {
    if (decision === "approved" && !window.confirm(`الموافقة على إلغاء الطلب ${req.order_code}؟ سيتم إلغاؤه فوراً.`)) return;
    setBusyId(req.id); setErr("");
    try {
      await api.post("/admin/cancel-requests/decide", {
        request_id: req.id,
        decision,
        admin_note: (noteMap[req.id] || "").trim() || null,
      });
      await load(statusTab);
    } catch (e) { setErr(extractError(e)); }
    finally { setBusyId(null); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
         onClick={onClose} data-testid="cancel-requests-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white w-full max-w-3xl rounded-3xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="p-5 flex items-center justify-between text-white"
             style={{background: "linear-gradient(135deg, #1E3A8A 0%, #2547D0 100%)"}}>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center">
              <XCircle size={22}/>
            </div>
            <div>
              <div className="font-black text-lg" style={{fontFamily:"Cairo"}}>طلبات الإلغاء</div>
              <div className="text-xs opacity-90">طلبات إلغاء بعد قبول السائق تنتظر مراجعتك</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load()} className="w-9 h-9 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center"
                    data-testid="cr-refresh"><RefreshCw size={15} className={loading ? "animate-spin" : ""}/></button>
            <button onClick={onClose} className="w-9 h-9 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center"
                    data-testid="cr-close"><X size={16}/></button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 px-5 py-3 border-b border-[var(--border)] bg-[var(--surface-alt)]">
          {[["pending", "قيد المراجعة"], ["approved", "موافق عليها"], ["rejected", "مرفوضة"]].map(([k, label]) => (
            <button key={k} onClick={() => setStatusTab(k)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-black transition ${statusTab === k ? "bg-primary-brand text-white" : "bg-white text-[var(--text-secondary)] border border-[var(--border)]"}`}
                    data-testid={`cr-tab-${k}`}>
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {err && <div className="bg-red-50 text-red-600 border border-red-200 px-3 py-2 rounded-lg text-sm">{err}</div>}
          {!loading && requests.length === 0 && (
            <div className="text-center py-10 text-[var(--text-secondary)]">
              <XCircle size={40} className="mx-auto mb-2 opacity-30"/>
              <div className="text-sm">لا توجد طلبات إلغاء {statusTab === "pending" ? "معلقة" : ""}</div>
            </div>
          )}
          {requests.map((req) => (
            <div key={req.id} className="rounded-2xl border border-[var(--border)] bg-white p-4 talabya-shadow-soft"
                 data-testid={`cr-${req.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-black text-primary-brand" style={{fontFamily:"Cairo"}}>#{req.order_code}</div>
                  <div className="text-[11px] text-[var(--text-secondary)] flex items-center gap-1 mt-0.5">
                    <Clock size={10}/> {fmtDate(req.created_at)}
                  </div>
                </div>
                {req.status === "pending" ? (
                  <span className="text-[10px] font-black bg-amber-100 text-amber-800 px-2 py-1 rounded">قيد المراجعة</span>
                ) : req.status === "approved" ? (
                  <span className="text-[10px] font-black bg-emerald-100 text-emerald-700 px-2 py-1 rounded">تمت الموافقة</span>
                ) : (
                  <span className="text-[10px] font-black bg-red-100 text-red-700 px-2 py-1 rounded">مرفوض</span>
                )}
              </div>
              <div className="text-xs">
                <div><b>الزبون:</b> {req.customer_name || "—"} <span dir="ltr" className="text-[var(--text-secondary)]">{req.customer_phone}</span></div>
                <div className="mt-2 bg-[var(--surface-alt)] rounded-lg p-2 flex items-start gap-2">
                  <MessageSquare size={12} className="mt-0.5 text-[var(--text-secondary)] shrink-0"/>
                  <div className="text-[var(--text-primary)]">{req.reason}</div>
                </div>
                {req.admin_note && (
                  <div className="mt-1 text-[11px] text-[var(--text-secondary)]">
                    <b>ملاحظة الإدارة:</b> {req.admin_note}
                  </div>
                )}
              </div>

              {req.status === "pending" && (
                <>
                  <input placeholder="ملاحظة داخلية (اختيارية)"
                         value={noteMap[req.id] || ""}
                         onChange={(e) => setNoteMap({ ...noteMap, [req.id]: e.target.value })}
                         className="w-full mt-3 px-3 py-2 rounded-lg border border-[var(--border)] bg-white focus:border-primary-brand focus:outline-none text-xs"
                         data-testid={`cr-note-${req.id}`}/>
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => decide(req, "approved")} disabled={busyId === req.id}
                            className="flex-1 py-2 rounded-lg bg-red-600 text-white text-xs font-black hover:bg-red-700 disabled:opacity-60 flex items-center justify-center gap-1"
                            data-testid={`cr-approve-${req.id}`}>
                      <CheckCircle2 size={12}/> موافقة على الإلغاء
                    </button>
                    <button onClick={() => decide(req, "rejected")} disabled={busyId === req.id}
                            className="flex-1 py-2 rounded-lg bg-white border-2 border-[var(--border)] text-[var(--text-primary)] text-xs font-black hover:bg-[var(--surface-alt)] disabled:opacity-60 flex items-center justify-center gap-1"
                            data-testid={`cr-reject-${req.id}`}>
                      <XCircle size={12}/> رفض الطلب
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
